import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";

export interface CropType {
  id: string;
  code: string;
  name: string;
  description: string;
  isActive: boolean;
  isAgroforestry: boolean;
  revenueAccountId?: string | null;
  revenueAccountCode?: string | null;
  revenueAccountName?: string | null;
  createdAt: string;
  // Lifecycle Defaults (all optional)
  sowingToGerminationDays?: number;
  sowingToSeedlingDays?: number;
  sowingToTransplantReadyDays?: number;
  sowingToOvergrownDays?: number;
  acclimationDays?: number;
  plantingToMaturityDays?: number;
  harvestingPeriodDays?: number;
  /** Optional 0–100 percentage of germinated stock that becomes sellable. Null = use system default of 70%. */
  stockPercent?: number;
}

export interface CreateCropTypeRequest {
  code: string;
  name: string;
  description: string;
  isAgroforestry: boolean;
  revenueAccountId?: string | null;
  sowingToGerminationDays?: number;
  sowingToSeedlingDays?: number;
  sowingToTransplantReadyDays?: number;
  sowingToOvergrownDays?: number;
  acclimationDays?: number;
  plantingToMaturityDays?: number;
  harvestingPeriodDays?: number;
  stockPercent?: number;
}

export interface UpdateCropTypeRequest {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  isAgroforestry: boolean;
  revenueAccountId?: string | null;
  sowingToGerminationDays?: number;
  sowingToSeedlingDays?: number;
  sowingToTransplantReadyDays?: number;
  sowingToOvergrownDays?: number;
  acclimationDays?: number;
  plantingToMaturityDays?: number;
  harvestingPeriodDays?: number;
  stockPercent?: number;
}

export type CropTypeSortBy = "name" | "code" | "createdAt";

export interface CropTypeListQuery {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  sortBy?: CropTypeSortBy;
  sortDirection?: "asc" | "desc";
  isActive?: boolean;
  isAgroforestry?: boolean;
}

export interface CropTypeListResult {
  items: CropType[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export const cropTypesApi = {
  getCropTypesPaged: async (
    query: CropTypeListQuery = {}
  ): Promise<CropTypeListResult> => {
    const params = new URLSearchParams();
    if (query.pageNumber != null)
      params.set("pageNumber", String(query.pageNumber));
    if (query.pageSize != null) params.set("pageSize", String(query.pageSize));
    if (query.search) params.set("search", query.search);
    if (query.sortBy) params.set("sortBy", query.sortBy);
    if (query.sortDirection) params.set("sortDirection", query.sortDirection);
    if (query.isActive != null)
      params.set("isActive", String(query.isActive));
    if (query.isAgroforestry != null)
      params.set("isAgroforestry", String(query.isAgroforestry));
    const qs = params.toString();
    const { data } = await apiClient.get<ApiResponse<CropTypeListResult>>(
      qs ? `/CropTypes/paged?${qs}` : "/CropTypes/paged"
    );
    return data.data;
  },

  getActiveCropTypes: async (): Promise<CropType[]> => {
    const { data } = await apiClient.get<ApiResponse<CropType[]>>(
      "/CropTypes",
      {
        params: { activeOnly: true },
      }
    );
    return data.data;
  },

  getAllCropTypes: async (): Promise<CropType[]> => {
    const { data } = await apiClient.get<ApiResponse<CropType[]>>(
      "/CropTypes"
    );
    return data.data;
  },

  getCropTypeById: async (id: string): Promise<CropType> => {
    const { data } = await apiClient.get<ApiResponse<CropType>>(
      `/CropTypes/${id}`
    );
    return data.data;
  },

  createCropType: async (
    payload: CreateCropTypeRequest
  ): Promise<CropType> => {
    const { data } = await apiClient.post<ApiResponse<CropType>>(
      "/CropTypes",
      payload
    );
    return data.data;
  },

  updateCropType: async (
    payload: UpdateCropTypeRequest
  ): Promise<CropType> => {
    const { data } = await apiClient.put<ApiResponse<CropType>>(
      `/CropTypes/${payload.id}`,
      payload
    );
    return data.data;
  },

  deleteCropType: async (id: string): Promise<void> => {
    await apiClient.delete(`/CropTypes/${id}`);
  },
};